import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class StockGroup extends ModelObjectWithDirtyCheck<StockGroup> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    /**
    * Restricted Stock - Raises warning on attempt to over-sell.
    */
    public restrictedStock: boolean = false;
    public isHidden: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.restrictedStock = jsonSerializedObject.restrictedStock;
          this.isHidden = jsonSerializedObject.isHidden;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.restrictedStock = this.restrictedStock;
        toRet.isHidden = this.isHidden;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "StockGroup.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
    ];
}


export class StockGroupService extends ServiceBase {

  public async getAllStockGroups(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(StockGroup)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.stockGroups.map((v: any) => new StockGroup(v));;
    return this.makeJsonRequest("stockGroup/v1/getAllStockGroups", req, false, extractor, opts);
  };

  public async getStockGroup(stockGroupId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<StockGroup> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockGroupId:stockGroupId.toString(),
    };
    const extractor = (response:any) => new StockGroup(response.stockGroup);;
    return this.makeJsonRequest("stockGroup/v1/getStockGroup", req, false, extractor, opts);
  };

  public async getStockGroups(stockGroupIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(StockGroup)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockGroupIds:stockGroupIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.stockGroups.map((v: any) => new StockGroup(v));;
    return this.makeJsonRequest("stockGroup/v1/getStockGroups", req, false, extractor, opts);
  };

  public async saveStockGroup(stockGroup: StockGroup, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockGroup:stockGroup.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("stockGroup/v1/saveStockGroup", req, true, extractor, opts);
  };

  public async setStockGroupIsHidden(stockGroupId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockGroupId:stockGroupId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("stockGroup/v1/setStockGroupIsHidden", req, true, extractor, opts);
  };
}

export const stockGroupService = new StockGroupService();